import * as RS from '@radix-ui/react-select';
import { useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { FieldShell, inputBase } from './Field';
import { useLocale } from '@/lib/i18n/useLocale';

export interface SelectOption {
  value: string;
  label: ReactNode;
}

// Radix's Select.Item forbids an empty-string value (it's reserved for clearing
// the selection). Map empty strings to/from an internal sentinel so call sites
// can keep using '' to mean "no filter / unset".
const EMPTY_SENTINEL = '__empty__';
const toInternal = (v: string) => (v === '' ? EMPTY_SENTINEL : v);
const fromInternal = (v: string) => (v === EMPTY_SENTINEL ? '' : v);

export function Select({
  label,
  value,
  onValueChange,
  options,
  placeholder,
  required,
  error,
  disabled,
  className,
}: {
  label?: ReactNode;
  value: string | undefined;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { isRtl } = useLocale();
  return (
    <FieldShell label={label} htmlFor={id} required={required} error={error}>
      <RS.Root
        dir={isRtl ? 'rtl' : 'ltr'}
        value={value === undefined ? undefined : toInternal(value)}
        onValueChange={(v) => onValueChange(fromInternal(v))}
        disabled={disabled}
      >
        <RS.Trigger
          id={id}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          className={cn(
            inputBase,
            'flex items-center justify-between gap-sm text-start',
            error && 'border-signal-error',
            className,
          )}
        >
          <RS.Value placeholder={placeholder ?? t('ui.selectPlaceholder')} />
          <RS.Icon aria-hidden className="text-ink-mute">
            ▾
          </RS.Icon>
        </RS.Trigger>
        <RS.Portal>
          <RS.Content
            position="popper"
            sideOffset={4}
            className="z-[95] max-h-[var(--radix-select-content-available-height)] min-w-[var(--radix-select-trigger-width)] overflow-auto rounded-sm border-[0.5px] border-hairline bg-canvas-paper py-2xs shadow-nav-scrolled"
          >
            <RS.Viewport>
              {options.map((opt) => (
                <RS.Item
                  key={opt.value}
                  value={toInternal(opt.value)}
                  className="flex cursor-pointer items-center px-sm py-xs text-small text-ink-graphite outline-none data-[highlighted]:bg-canvas-sand data-[state=checked]:text-accent-blue"
                >
                  <RS.ItemText>{opt.label}</RS.ItemText>
                </RS.Item>
              ))}
            </RS.Viewport>
          </RS.Content>
        </RS.Portal>
      </RS.Root>
    </FieldShell>
  );
}
